/* eslint-disable no-shadow */
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import cx from 'classnames';
import {
  Anniversary,
  Briefcase,
  Birthday,
  Button,
  Checkmark,
  Chevron,
  getTranslation,
  Graduation,
  Icon,
  Modal,
  TranslationProvider,
  getDatadogFeatureFlagsClient,
  getIsUSFromState,
  initDatadogFeatureFlags,
  focusTrap,
} from '../../../../../ui-kitchen/src';
import ClampLines from '../../../../../ui-kitchen/src/components/ClampLines/ClampLines';
import {
  deleteConsumerNotes,
  deleteDietaryNote,
  deletePartyGuest,
  getConsumerGuestTags,
  getConsumerReservationPartyTags,
  getTags,
  handleReservationSpecialRequest,
  postConsumerGuestTags,
  postConsumerNotes,
  postConsumerNotesList,
  postConsumerReservationPartyTags,
  postDietaryNote,
  postDietaryNotesList,
  postPartyInviteDecline,
} from '../../../../../ui-kitchen/src/api/api';

import reservationShape from '../ConfirmationPage/reservationShape';

import './ReservationQuestionnaire.scss';
import AllergyButtonGroup from './components/AllergyButtonGroup';
import { getUserThunk } from '../../../../../ui-kitchen/src/reducers/userSlice';
import Logger from '../../../../../ui-kitchen/src/utils/logger';
import GuestInvite from '../GuestInvite';
import ReservationQuestion from './ReservationQuestion';
import shareSVG from '../../../../../ui-kitchen/src/svg/icons/ic_share_20x20.svg';
import CloseThick from '../../svg/dynamic/ic_close_20x20.svg';
import inFrame from '../../utils/inFrame';
import getLocale from '../../utils/i18n/getLocale';
import IypInviteModal from './IypInviteModal';
import {
  DATADOG_APPLICATION_ID,
  DATADOG_APPLICATION_NAME,
  checkIsOwnReservation,
  copyInviteLinkWithShareSheet,
  getIypAnalyticsPayload,
  getIypModalInviteContext,
  IYP_MODAL_CLOSE_DELAY_MS,
  IYP_MODAL_DELAY_MS,
  IYP_MODAL_FLAG_KEY,
  IYP_SHARE_INVITE_ACTION_NAME,
  IYP_SHARE_INVITE_CTA_CONTEXT,
  IYP_SHARE_INVITE_SURFACE,
  isEligibleForIypModal,
  isIypModalInviteContext,
} from './reservationQuestionnaireInviteFlow';

const testId = 'guest-invite';

const PAGE_CONTEXTS = {
  ACCOUNT: 'Confirmation - Account',
  POST_BOOKING: 'Confirmation - Post Booking',
};

const OCCASION_ICON_MAP = {
  business: Briefcase,
  cake: Birthday,
  heart: Anniversary,
  graduation: Graduation,
};

const SECTIONS = {
  OCCASION: 'occasion',
  ALLERGIES: 'allergies',
  REQUESTS: 'requests',
  PARTY: 'party',
};

const DEFAULT_SECTION_STATE = {
  [SECTIONS.OCCASION]: false,
  [SECTIONS.ALLERGIES]: false,
  [SECTIONS.REQUESTS]: false,
  [SECTIONS.PARTY]: false,
};

const ALLERGY_GROUPS = {
  YOU: 'For You',
  YOUR_PARTY: 'Your Party',
};

const addSpecialRequests = getTranslation({
  translationKey: 'label_special_request',
  fallback: 'Add special requests',
});

function analyticsPayload(reservation) {
  return {
    venue: reservation.venue.name,
    city: reservation.venue.location.city,
  };
}

function getAuthModalStyles(breakpoints) {
  const desktopStyles = {
    borderRadius: '10px',
    height: 'fit-content',
    top: '10%',
  };

  const mobileStyles = {
    top: '15vh',
    left: 0,
    right: 0,
    bottom: 0,
    height: 'fit-content',
    maxHeight: '300px',
    margin: '0 auto',
    maxWidth: '300px',
  };

  let breakpointStyles = desktopStyles;

  if (breakpoints.xs && !breakpoints.sm) {
    breakpointStyles = mobileStyles;
  }

  return {
    overlay: {
      alignItems: 'flex-end',
      backgroundColor: 'rgba(0, 0, 0, 0.25)',
      display: 'flex',
      zIndex: '10',
    },
    content: {
      backgroundColor: '#fff',
      border: 'none',
      boxShadow: '0 10px 50px 0 rgba(0, 0, 0, 0.1)',
      margin: '0 auto',
      maxWidth: '400px',
      textAlign: 'center',
      ...breakpointStyles,
    },
  };
}

class ReservationQuestionnaire extends React.Component {
  state = {
    activeSections: DEFAULT_SECTION_STATE,
    allergyTags: [],
    allergyGroup: ALLERGY_GROUPS.YOU,
    allergyQuestionComplete: false,
    dietaryTags: [],
    error: null,
    isLoading: true,
    occasionTags: [],
    partyTags: {},
    selections: {
      occasion: null,
      dietaryNoteGuest: null,
      dietaryNoteGuestId: null,
      dietaryNoteParty: null,
      dietaryNotePartyId: null,
      specialRequest: null,
    },
    userId: null,
    iypExperimentUserId: null,
    userTags: {},
    ariaLiveText: null,
    copyLinkActive: false,
    guestToRemove: null,
    showIypModal: false,
    isIypModalFeatureFlagEnabled: false,
    hasShownIypModalInFlow: false,
    /* eslint-disable-next-line react/destructuring-assignment */
    party: this.props.reservation?.party,
    showDeclineModal: false,
    showRemoveGuestModal: false,
  }

  static defaultProps = {
    geoGateForDataDogExperiment: {},
    sourceId: undefined,
  }

  componentDidMount() {
    const {
      reservation,
    } = this.props;

    if (!reservation) {
      return;
    }

    this.initReservationQuestionnaireAndIypExperiment();
  }

  componentDidUpdate(prevProps) {
    const {
      reservation,
    } = this.props;

    if (!prevProps.reservation && reservation) {
      this.initReservationQuestionnaireAndIypExperiment();
      return;
    }

    const didIypDatadogContextChange = prevProps.reservation?.token !== reservation?.token
      || prevProps.reservation?.attendee?.token !== reservation?.attendee?.token;

    if (reservation && didIypDatadogContextChange) {
      this.refreshIypModalFeatureFlag();
    }
  }

  componentWillUnmount() {
    if (this.iypModalTimer) {
      window.clearTimeout(this.iypModalTimer);
    }
    if (this.iypModalCloseTimer) {
      window.clearTimeout(this.iypModalCloseTimer);
    }
    if (this.copyLinkFeedbackTimer) {
      window.clearTimeout(this.copyLinkFeedbackTimer);
    }
  }

  init = async () => {
    const {
      reservation,
      getUserThunk,
    } = this.props;

    let allTags = {};

    try {
      allTags = await getTags();
    } catch (error) {
      this.setState({ error });
      Logger.error('[ReservationQuestionnaire] API failure getTags: ', error);
      return;
    }

    const allergyTags = [];
    const dietaryTags = [];
    const occasionTags = [];

    if (allTags?.tags) {
      allTags.tags.forEach((item) => {
        if (item.category_id === 1 && item.group_id === 1) {
          allergyTags.push(item);
        } else if (item.category_id === 1 && item.group_id === 10) {
          dietaryTags.push(item);
        } else if (item.category_id === 2 && item.group_id === 6) {
          occasionTags.push(item);
        }
      });
    }

    let userId = null;
    let iypExperimentUserId = null;
    try {
      const user = await getUserThunk();
      userId = user.guest_id;
      iypExperimentUserId = user.id;
    } catch (error) {
      Logger.error('[ReservationQuestionnaire] failed to fetch user ID: ', error);
    }

    let userTagsResponse = {};
    try {
      userTagsResponse = await getConsumerGuestTags();
    } catch (error) {
      Logger.error('[ReservationQuestionnaire] API failure getConsumerGuestTags(): ', error);
    }
    let userTags = {};
    if (userTagsResponse?.tags) {
      userTags = userTagsResponse.tags.reduce((memo, tag) => {
        // eslint-disable-next-line no-param-reassign
        memo[tag.id] = tag.name;
        return memo;
      }, {});
    }

    let partyTagsResponse = {};
    try {
      partyTagsResponse = await getConsumerReservationPartyTags(reservation.id);
    } catch (error) {
      Logger.error('[ReservationQuestionnaire] API failure getConsumerReservationPartyTags: ', error);
    }

    const partyTags = partyTagsResponse.tags?.reduce((memo, tag) => {
      if (tag.category_id !== 2) {
        // eslint-disable-next-line no-param-reassign
        memo[tag.id] = tag.name;
      }
      return memo;
    }, {}) || {};

    let savedGuestDietaryNotes = null;
    let savedGuestDietaryNoteId = null;
    try {
      const response = await postDietaryNotesList({ id: userId });
      const notesLength = response.entity.notes.length;
      if (notesLength > 0) {
        const note = response.entity.notes[notesLength - 1];
        savedGuestDietaryNotes = note.body;
        savedGuestDietaryNoteId = note.id;
      }
    } catch (error) {
      Logger.error('[ReservationQuestionnaire] API failure postDietaryNotesList: ', error);
    }

    let savedPartyDietaryNotes = null;
    let savedPartyDietaryNoteId = null;
    try {
      const response = await postConsumerNotesList({ partyId: reservation.id });
      const notesLength = response.entity?.notes?.length;
      if (notesLength > 0) {
        const note = response.entity.notes[notesLength - 1];
        savedPartyDietaryNotes = note.body;
        savedPartyDietaryNoteId = note.id;
      }
    } catch (error) {
      Logger.error('[ReservationQuestionnaire] API failure postConsumerNotesList: ', error);
    }

    const windowHash = window.location.hash;
    const context = this.getPageContext(windowHash);
    const {
      occasion,
      specialRequest,
    } = reservation;

    const isHost = reservation.attendee.type === 'host';
    let activeSections = DEFAULT_SECTION_STATE;

    if (!isHost) {
      activeSections = {
        [SECTIONS.OCCASION]: false,
        [SECTIONS.ALLERGIES]: true,
        [SECTIONS.REQUESTS]: false,
        [SECTIONS.PARTY]: false,
      };
    }

    if (context === PAGE_CONTEXTS.POST_BOOKING) {
      activeSections = {
        [SECTIONS.OCCASION]: true,
        [SECTIONS.ALLERGIES]: false,
        [SECTIONS.REQUESTS]: false,
        [SECTIONS.PARTY]: false,
      };
    }

    let occasionName = occasion;

    if (occasionName === 'null' || occasionName === '') {
      occasionName = null;
    }

    const selectionState = {
      occasion: { name: occasionName },
      dietaryNoteGuest: savedGuestDietaryNotes,
      dietaryNoteGuestId: savedGuestDietaryNoteId,
      dietaryNoteParty: savedPartyDietaryNotes,
      dietaryNotePartyId: savedPartyDietaryNoteId,
      specialRequest,
    };

    this.setState({
      activeSections,
      allergyTags,
      dietaryTags,
      error: null,
      isLoading: false,
      occasionTags,
      party: reservation.party,
      partyTags,
      selections: selectionState,
      iypExperimentUserId,
      userId,
      userTags,
    });

    // eslint-disable-next-line consistent-return
    return { iypExperimentUserId };
  }

  getPageContext = (hash) => {
    const parsedHash = hash?.replace(/^#\//, '').split('?')[0] || '';
    let context = '';
    if (parsedHash.startsWith('account')) {
      context = PAGE_CONTEXTS.ACCOUNT;
    } else if (parsedHash.startsWith('confirmation')) {
      context = PAGE_CONTEXTS.POST_BOOKING;
    }

    return context;
  }

  markIypModalAsShown = () => {
    this.setState({ hasShownIypModalInFlow: true });
  }

  initReservationQuestionnaireAndIypExperiment = async () => {
    const { iypExperimentUserId } = await this.init() || {};
    await this.refreshIypModalFeatureFlag({ iypExperimentUserId });
  }

  getIypTargetingKey = () => {
    const { iypExperimentUserId } = this.state;
    return iypExperimentUserId ? String(iypExperimentUserId) : 'unknown_resy_user';
  }

  getIypGuestsCount = () => {
    const { reservation } = this.props;
    return reservation?.seats || reservation?.party?.length || 1;
  }

  getDeviceType = () => {
    const isMobileDevice = window?.navigator?.userAgentData?.mobile;
    return isMobileDevice ? 'Mobile Device' : 'Desktop';
  }

  getDatadogApplicationContext = () => {
    const ddRumConfig = window?.DD_RUM?.getInitConfiguration?.() || {};
    return {
      applicationId: ddRumConfig.applicationId || DATADOG_APPLICATION_ID,
      applicationName: ddRumConfig.service || DATADOG_APPLICATION_NAME,
    };
  }

  getIypUserId = () => {
    const { iypExperimentUserId } = this.state;
    return iypExperimentUserId || null;
  }

  refreshIypModalFeatureFlag = async ({ iypExperimentUserId } = {}) => {
    const { reservation, matchMedia } = this.props;
    const { iypExperimentUserId: stateIypExperimentUserId } = this.state;
    if (!reservation) {
      return;
    }

    const resolvedUserId = iypExperimentUserId ?? stateIypExperimentUserId;
    const isMobileWeb = !matchMedia?.md && !inFrame();
    const guestsCount = this.getIypGuestsCount();
    const deviceType = this.getDeviceType();
    const { applicationId, applicationName } = this.getDatadogApplicationContext();

    // Datadog evaluation context:
    // - targetingKey: stable Resy user ID for deterministic bucketing across sessions.
    // - reservation_token: useful for experiment/debug correlation on reservation-scoped flows.
    // Example resulting context:
    // {
    //   targetingKey: "98374",
    //   reservation_token: "resy_token_xyz789"
    // }
    const context = {
      targetingKey: resolvedUserId ? String(resolvedUserId) : 'unknown_resy_user',
      reservation_token: reservation?.token,
      deviceType,
      applicationId,
      applicationName,
      isMobileWeb,
      guestsCount,
    };

    try {
      await initDatadogFeatureFlags({
        context,
      });
      const featureFlagsClient = getDatadogFeatureFlagsClient();
      const isIypModalFeatureFlagEnabled = featureFlagsClient.getBooleanValue(IYP_MODAL_FLAG_KEY, false);
      this.setState({ isIypModalFeatureFlagEnabled }, () => {
        this.maybeScheduleIypModal();
      });
    } catch (error) {
      Logger.error('[ReservationQuestionnaire] failed to evaluate IYP Datadog feature flag.', error);
      this.setState({ isIypModalFeatureFlagEnabled: false });
    }
  }

  isEligibleForIypModal = () => {
    const {
      geoGateForDataDogExperiment,
      matchMedia,
      reservation,
    } = this.props;
    const {
      hasShownIypModalInFlow,
      isIypModalFeatureFlagEnabled,
    } = this.state;

    if (!isIypModalFeatureFlagEnabled || !getIsUSFromState(geoGateForDataDogExperiment)) {
      return false;
    }

    return isEligibleForIypModal({
      getPageContext: this.getPageContext,
      hasShownIypModalInFlow,
      hash: window.location.hash,
      inFrameCheck: inFrame,
      matchMedia,
      pageContexts: PAGE_CONTEXTS,
      reservation,
    });
  }

  maybeScheduleIypModal = () => {
    if (!this.isEligibleForIypModal()) {
      return;
    }

    if (this.iypModalTimer) {
      window.clearTimeout(this.iypModalTimer);
    }

    this.iypModalTimer = window.setTimeout(() => {
      this.setState({ showIypModal: true });
      this.markIypModalAsShown();
    }, IYP_MODAL_DELAY_MS);
  }

  getIypAnalyticsPayload = () => {
    const { reservation } = this.props;
    return getIypAnalyticsPayload(reservation);
  }

  trackIypEvent = (eventName, eventPayload = {}) => {
    const { Analytics } = this.props;
    const payload = {
      ...this.getIypAnalyticsPayload(),
      ...eventPayload,
    };
    Analytics?.event?.(eventName, payload);

    if (typeof window !== 'undefined' && window.DD_RUM?.addAction) {
      window.DD_RUM.addAction(eventName, payload);
    }
  }

  trackIypShareInviteAction = (context) => {
    const { Analytics } = this.props;

    let ctaContext;
    let inviteSurface;
    if (context === 'Confirmation - IYP Modal Avatar') {
      ctaContext = IYP_SHARE_INVITE_CTA_CONTEXT.MODAL_AVATAR;
      inviteSurface = IYP_SHARE_INVITE_SURFACE.MODAL;
    } else if (context === 'Confirmation - IYP Modal Invite') {
      ctaContext = IYP_SHARE_INVITE_CTA_CONTEXT.MODAL_INVITE_BUTTON;
      inviteSurface = IYP_SHARE_INVITE_SURFACE.MODAL;
    } else if (context === 'Confirmation - Copy Link CTA' || context === 'Confirmation - unconfirmed') {
      ctaContext = IYP_SHARE_INVITE_CTA_CONTEXT.NON_MODAL_INVITE_BUTTON;
      inviteSurface = IYP_SHARE_INVITE_SURFACE.NON_MODAL;
    }

    if (!ctaContext || !inviteSurface) {
      return;
    }

    const payload = {
      ...this.getIypAnalyticsPayload(),
      ctaContext,
      inviteSurface,
    };

    Analytics?.event?.(IYP_SHARE_INVITE_ACTION_NAME, payload);

    if (typeof window !== 'undefined' && window.DD_RUM?.addAction) {
      window.DD_RUM.addAction(IYP_SHARE_INVITE_ACTION_NAME, payload);
    }
  }

  closeIypModal = () => {
    this.setState({ showIypModal: false });
  }

  getIypModalInviteContext = (tapTarget) => getIypModalInviteContext(tapTarget)

  trackShareInvite = (context) => {
    const { Analytics, reservation } = this.props;
    if (isIypModalInviteContext(context)) {
      return;
    }

    Analytics?.event?.('share invite', { ...analyticsPayload(reservation), context });
  }

  onIypInviteTap = async (tapTarget) => {
    const context = this.getIypModalInviteContext(tapTarget);
    const didCopy = await this.copyInviteLink(context);
    if (!didCopy) {
      return;
    }

    if (this.iypModalCloseTimer) {
      window.clearTimeout(this.iypModalCloseTimer);
    }
    this.iypModalCloseTimer = window.setTimeout(() => {
      this.closeIypModal();
    }, IYP_MODAL_CLOSE_DELAY_MS);
  }

  renderIypModal = () => {
    const { ariaLiveText, copyLinkActive, showIypModal } = this.state;
    const { matchMedia, reservation } = this.props;
    if (!showIypModal) {
      return null;
    }

    return (
      <IypInviteModal
        ariaLiveText={ariaLiveText}
        copyInviteLink={this.copyInviteLink}
        copyLinkActive={copyLinkActive}
        forceIsOpen={showIypModal}
        getPageContext={this.getPageContext}
        matchMedia={matchMedia}
        onDismiss={this.closeIypModal}
        pageContexts={PAGE_CONTEXTS}
        reservation={reservation}
        trackIypEvent={this.trackIypEvent}
      />
    );
  }

  toggleSection = (section) => {
    const { activeSections } = this.state;
    const selectedSectionState = activeSections[section];
    const newSectionState = {
      ...activeSections,
      [section]: !selectedSectionState,
    };
    this.setState({ activeSections: newSectionState });
  }

  setOccasion = (occasion) => {
    const {
      activeSections,
      selections,
    } = this.state;

    const {
      Analytics,
      reservation,
    } = this.props;
    const { hideAllergyQuestion } = reservation.venue;

    let selectedOccasionName = occasion.name;

    const isDeselecting = selections.occasion?.name === occasion.name;
    if (isDeselecting || occasion?.id === 'goodTime') {
      selectedOccasionName = '';

      handleReservationSpecialRequest({
        resyToken: reservation.token,
        conciergeResyToken: reservation.conciergeResyToken,
        occasionName: '',
        occasionId: '',
      });

      this.setState({
        selections: { ...selections, occasion: null },
        activeSections: {
          [SECTIONS.OCCASION]: isDeselecting,
          [SECTIONS.ALLERGIES]: !hideAllergyQuestion && !isDeselecting,
          [SECTIONS.REQUESTS]: (hideAllergyQuestion && !isDeselecting) || activeSections[SECTIONS.REQUESTS],
        },
      }, this.setFocus());
    } else {
      handleReservationSpecialRequest({
        resyToken: reservation.token,
        conciergeResyToken: reservation.conciergeResyToken,
        occasionName: occasion.name,
        occasionId: occasion.id,
      });

      Analytics.event('complete post-booking questionnaire', {
        'question type': 'occasion',
        value: occasion.name,
      });

      const shouldKeepQuestionOpen = selectedOccasionName === null;
      this.setState({
        selections: { ...selections, occasion },
        activeSections: {
          [SECTIONS.OCCASION]: shouldKeepQuestionOpen,
          [SECTIONS.ALLERGIES]: hideAllergyQuestion,
          [SECTIONS.PARTY]: true,
          [SECTIONS.REQUESTS]: hideAllergyQuestion || activeSections[SECTIONS.REQUESTS],
        },
      }, this.setFocus());
    }
  }

  toggleAllergyGroup = (selectedGroup) => {
    const {
      allergyGroup,
    } = this.state;
    if (allergyGroup === selectedGroup) {
      return;
    }
    this.setState({ allergyGroup: selectedGroup });
  }

  saveUserAllergy = (allergy) => {
    const {
      userTags,
    } = this.state;

    const {
      Analytics,
    } = this.props;

    let newTagState = {};

    if (userTags[allergy.id]) {
      newTagState = {
        ...userTags,
      };
      delete newTagState[allergy.id];
      this.setState({ userTags: newTagState });
    } else {
      newTagState = {
        ...userTags,
        [allergy.id]: allergy.name,
      };
      this.setState({ userTags: newTagState });

      Analytics.event('complete post-booking questionnaire', {
        'question type': 'guest restriction',
        value: allergy.name,
      });
    }

    const tagsToUpdate = Object.keys(newTagState);
    postConsumerGuestTags({ allergyIds: tagsToUpdate });
  }

  savePartyAllergy = (allergy) => {
    const {
      Analytics,
      reservation,
    } = this.props;

    const {
      partyTags,
    } = this.state;

    let newTagState = {};

    if (partyTags[allergy.id]) {
      newTagState = {
        ...partyTags,
      };
      delete newTagState[allergy.id];
      this.setState({ partyTags: newTagState });
    } else {
      newTagState = {
        ...partyTags,
        [allergy.id]: allergy.name,
      };
      this.setState({ partyTags: newTagState });

      Analytics.event('complete post-booking questionnaire', {
        'question type': 'party restriction',
        value: allergy.name,
      });
    }

    const tagsToUpdate = Object.keys(newTagState).map((tag) => ({ id: parseInt(tag, 10) }));
    postConsumerReservationPartyTags(reservation.id, tagsToUpdate);
  }

  areAllergiesSet = () => {
    const {
      partyTags,
      selections,
      userTags,
    } = this.state;

    return (
      Object.keys(userTags).length > 0
      || Object.keys(partyTags).length > 0
      || selections.dietaryNoteGuest
      || selections.dietaryNoteParty
    );
  }

  getAllergySubtitle = () => {
    const {
      partyTags = {},
      selections,
      userTags = {},
    } = this.state;

    const subtitleStrings = [
      ...Object.values(userTags),
      ...Object.values(partyTags),
    ];

    if (selections.dietaryNoteGuest) {
      subtitleStrings.push(selections.dietaryNoteGuest);
    }

    if (selections.dietaryNoteParty) {
      subtitleStrings.push(selections.dietaryNoteParty);
    }

    if (subtitleStrings.length === 0) {
      return '';
    }

    return new Intl.ListFormat(getLocale(), { style: 'long', type: 'unit' }).format(subtitleStrings);
  }

  unfilledContinueButton = (onSubmit) => {
    const { reservation } = this.props;

    return (
      <TranslationProvider translationKey="none_continue">
        <Button
          variant="primary"
          disabled={reservation?.pastOrFuture !== 'future'}
          onClick={onSubmit}
        >
          None, continue
        </Button>
      </TranslationProvider>
    );
  };

  filledContinueButton = (onSubmit) => {
    const { reservation } = this.props;

    return (
      <TranslationProvider translationKey="continue">
        <Button
          variant="primary"
          disabled={reservation?.pastOrFuture !== 'future'}
          onClick={onSubmit}
        >
          Continue
        </Button>
      </TranslationProvider>
    );
  };

  isSpecialRequestSet = () => {
    const { selections } = this.state;
    const { specialRequest } = selections;

    return typeof specialRequest === 'string' && specialRequest !== 'undefined' && specialRequest !== 'null' && specialRequest !== '';
  }

  renderDietaryNoteInput = (group) => {
    const { selections } = this.state;
    const { reservation } = this.props;

    let nodeId = 'dietaryNote-guest';
    let handleChange = (response) => this.setDietaryNote(response, ALLERGY_GROUPS.YOU);
    let defaultVal = selections.dietaryNoteGuest;
    let name = 'dietary-note-guest';
    let disclosure = (
      <TranslationProvider translationKey="allergy_note_disclosure_guest">
        <div className="allergy-note-disclosure">
          We will share allergies, diet restrictions, or preferences you provide with our restaurants.
        </div>
      </TranslationProvider>
    );

    if (group === ALLERGY_GROUPS.YOUR_PARTY) {
      nodeId = 'dietaryNote-party';
      handleChange = (response) => this.setDietaryNote(response, ALLERGY_GROUPS.YOUR_PARTY);
      defaultVal = selections.dietaryNoteParty;
      name = 'dietary-note-party';
      disclosure = (
        <TranslationProvider translationKey="allergy_note_disclosure_party">
          <div className="allergy-note-disclosure">
            We will share allergies, diet restrictions, or preferences you provide with the restaurant in advance of your reservation.
          </div>
        </TranslationProvider>
      );
    }

    const addNote = getTranslation({ translationKey: 'add_note', fallback: 'Add an optional note on allergies and restrictions...' });
    return (
      <>
        <textarea
          aria-label={getTranslation({
            translationKey: 'additional_dietary_note',
            fallback: 'Additional Dietary Note',
          })}
          className="additional-note"
          disabled={reservation?.pastOrFuture !== 'future'}
          id={nodeId}
          name={name}
          onChange={handleChange}
          placeholder={addNote}
          defaultValue={defaultVal}
        />
        {disclosure}
      </>
    );
  }

  setFocus = () => {
    const { activeSections } = this.state;
    const { reservation } = this.props;

    const skipAllergies = reservation?.venue?.hideAllergyQuestion;

    const activeSectionNextElementMap = {
      occasion: 1,
      allergies: skipAllergies ? null : 2,
      requests: null,
    };

    const sectionElementClassToTarget = '.ReservationQuestionnaire__question--header';
    const truthyValuesExist = Object.values(activeSections).reduce((prev, curr) => (prev === true || curr === true), []);

    if (!truthyValuesExist) {
      document.querySelector('.GuestInvite__cta')?.focus();
    } else {
      const currentActiveSection = Object.keys(activeSections).find((section) => activeSections[section] === true);
      const currentSectionListLocation = activeSectionNextElementMap[currentActiveSection];

      if (currentSectionListLocation === null) {
        document.querySelector('.GuestInvite__cta')?.focus();
        return;
      }

      const availableSectionNodes = document.querySelectorAll(sectionElementClassToTarget);
      const correctElement = availableSectionNodes[currentSectionListLocation];

      if (correctElement) {
        correctElement.focus();
      }
    }
  }

  setDietaryNote = (response, group) => {
    const { selections } = this.state;

    let newSelectionState = {
      ...selections,
      dietaryNoteGuest: response.currentTarget.value,
    };

    if (group === ALLERGY_GROUPS.YOUR_PARTY) {
      newSelectionState = {
        ...selections,
        dietaryNoteParty: response.currentTarget.value,
      };
    }

    this.setState({ selections: newSelectionState });
  }

  setSpecialRequest = (response) => {
    const { selections } = this.state;

    const newSelectionState = {
      ...selections,
      specialRequest: response.currentTarget.value || '',
    };

    this.setState({ selections: newSelectionState });
  }

  setActiveSpecialRequestSubtitle = (activeSections, selections) => (activeSections[SECTIONS.REQUESTS]
    ? (
      <TranslationProvider translationKey="well_give_restaurants_heads_up">
        <div
          className="ReservationQuestionnaire__question--subtitle"
        >
          We&apos;ll give restaurants a heads up.
        </div>
      </TranslationProvider>

    ) : (
      <div
        className="ReservationQuestionnaire__question--subtitle"
      >
        <ClampLines
          text={selections.specialRequest}
          id="special-request-subtitle"
          lines={1}
          buttons={false}
          ellipsis="..."
        />
      </div>

    )
  )

  submitDietaryNote = async () => {
    const {
      activeSections,
      selections,
      userId,
    } = this.state;

    const {
      Analytics,
      reservation,
    } = this.props;

    let success = true;

    let newGuestNoteId = null;

    if (selections.dietaryNoteGuest) {
      try {
        const response = await postDietaryNote({
          body: selections.dietaryNoteGuest,
          id: userId,
          noteId: selections.dietaryNoteGuestId,
        });

        newGuestNoteId = response.note.id;

        Analytics.event('complete post-booking questionnaire', {
          'question type': 'guest dietary note',
          value: selections.dietaryNoteGuest,
        });
      } catch (error) {
        success = false;
        Logger.error('[ReservationQuestionnaire] error saving guest dietary note: ', error);
      }
    } else if (selections.dietaryNoteGuestId) {
      try {
        deleteDietaryNote({ entityId: userId, noteId: selections.dietaryNoteGuestId });
      } catch (error) {
        Logger.error('[ReservationQuestionnaire] error deleting guest dietary note: ', error);
      }
    }

    let newPartyNoteId = null;

    if (selections.dietaryNoteParty) {
      try {
        const response = await postConsumerNotes({
          body: selections.dietaryNoteParty,
          id: reservation.id,
          noteId: selections.dietaryNotePartyId,
        });

        newPartyNoteId = response.note?.id;

        Analytics.event('complete post-booking questionnaire', {
          'question type': 'party dietary note',
          value: selections.dietaryNoteParty,
        });
      } catch (error) {
        success = false;
        Logger.error('[ReservationQuestionnaire] error saving party dietary note: ', error);
      }
    } else if (selections.dietaryNotePartyId) {
      try {
        deleteConsumerNotes({ entityId: reservation.id, noteId: selections.dietaryNotePartyId });
      } catch (error) {
        Logger.error('[ReservationQuestionnaire] error deleting party dietary note: ', error);
      }
    }

    if (success) {
      const newSectionState = {
        ...activeSections,
        [SECTIONS.ALLERGIES]: false,
        [SECTIONS.REQUESTS]: true,
      };

      const newSelectionState = {
        ...selections,
        dietaryNoteGuestId: newGuestNoteId,
        dietaryNotePartyId: newPartyNoteId,
      };

      this.setState({
        activeSections: newSectionState,
        allergyQuestionComplete: true,
        selections: newSelectionState,
      }, this.setFocus());
    }
  }

  submitSpecialRequest = () => {
    const {
      activeSections,
      selections,
    } = this.state;

    const {
      Analytics,
      reservation,
    } = this.props;

    handleReservationSpecialRequest({
      resyToken: reservation.token,
      conciergeResyToken: reservation.conciergeResyToken,
      request: selections.specialRequest || '',
    });

    const newSectionState = {
      ...activeSections,
      [SECTIONS.REQUESTS]: false,
    };

    Analytics.event('complete post-booking questionnaire', {
      'question type': 'special request',
      value: selections.specialRequest,
    });

    this.setState({ activeSections: newSectionState }, this.setFocus());
  }

  getHeaderTranslationKey = (section) => {
    switch (section) {
      case SECTIONS.OCCASION:
        return 'whats_the_occasion';
      case SECTIONS.ALLERGIES:
        return 'allergies_or_restrictions';
      case SECTIONS.REQUESTS:
        return 'any_special_requests';
      case SECTIONS.PARTY:
        return 'invite_your_party_title';
      default:
        return null;
    }
  }

  getHeaderTranslationString = (section) => {
    switch (section) {
      case SECTIONS.OCCASION:
        return 'What&apos;s the occasion?';
      case SECTIONS.ALLERGIES:
        return 'Any allergies or restrictions?';
      case SECTIONS.REQUESTS:
        return 'Any special requests?';
      case SECTIONS.PARTY:
        return 'Who\'s joining?';
      default:
        return null;
    }
  }

  getHeaderButtonAriaLabel = (section) => {
    const header = getTranslation({
      translationKey: this.getHeaderTranslationKey(section),
      fallback: this.getHeaderTranslationString(section),
    });
    if (!this.areAllergiesSet()) {
      return header;
    }

    const allergySubtitle = this.getAllergySubtitle();
    return getTranslation({
      translationKey: 'question_selected_values_aria_label',
      translationValues: {
        header,
        values: allergySubtitle,
      },
      fallback: `${header} Selected: ${allergySubtitle}.`,
    });
  }

  renderPartyActions = () => {
    const {
      reservation: {
        party,
        attendee,
        pastOrFuture,
      } = {},
      sourceId,
    } = this.props;
    const isHost = attendee?.type === 'host';
    const { copyLinkActive } = this.state;

    if (party?.length === 1 || pastOrFuture !== 'future') return null;

    let partyOptions = {
      copyText: 'Leave',
      copyTextKey: 'leave',
      button: {
        variant: 'secondary',
        onClick: this.toggleDeclineModal,
        'data-test-id': 'guest-invite-remove-myself',
      },
    };

    if (isHost) {
      partyOptions = {
        copyText: copyLinkActive ? 'Copied!' : 'Share',
        copyTextKey: copyLinkActive ? 'button_copied_bang' : 'share',
        shareIcon: true,
        button: {
          variant: 'black-small',
          'aria-checked': true,
          'aria-live': 'polite',
          onClick: () => this.copyInviteLink('Confirmation - Copy Link CTA'),
          'data-test-id': `guest-invite-copy-link${copyLinkActive ? '-active' : ''}`,
        },
      };
    }

    return (
      <div className={cx(
        'ReservationQuestionnaire__question--invite-your-party',
        { 'ReservationQuestionnaire__question--invite-your-party-host': isHost },
        { 'ReservationQuestionnaire__question--invite-your-party-openai': sourceId === 'oai_widget' }
      )}
      >
        <Button
          sm
          className="ReservationQuestionnaire__question--share-invite"
          {...partyOptions.button}
        >
          {partyOptions.shareIcon && <Icon component={shareSVG} height="16" name="upload" width="16" />}
          <TranslationProvider translationKey={partyOptions.copyTextKey}>
            <span>{partyOptions.copyText}</span>
          </TranslationProvider>
        </Button>
      </div>
    );
  }

  // Invite your Party methods, moved from GuestInvite component
  copyInviteLink = async (context) => {
    const {
      matchMedia,
      reservation,
    } = this.props;

    // Datadog Experiment for IYP Modal
    this.trackIypShareInviteAction(context);

    if (!checkIsOwnReservation(reservation)) {
      return false;
    }

    this.trackShareInvite(context);

    const { didCopy, method } = await copyInviteLinkWithShareSheet({
      context,
      matchMedia,
      reservation,
    });

    if (method !== 'clipboard') {
      return didCopy;
    }

    if (this.copyLinkFeedbackTimer) {
      window.clearTimeout(this.copyLinkFeedbackTimer);
    }

    this.setState({
      ariaLiveText: getTranslation({ translationKey: 'copied_invite_link', fallback: 'Copied invite link' }),
      copyLinkActive: true,
    }, () => context === 'Confirmation - Copy Link CTA' && document.querySelector('.GuestInvite__cta')?.focus());

    this.copyLinkFeedbackTimer = window.setTimeout(() => {
      this.setState({
        ariaLiveText: null,
        copyLinkActive: false,
      }, () => context === 'Confirmation - Copy Link CTA' && document.querySelector('.GuestInvite__cta')?.focus());
    }, 2000);

    return didCopy;
  }

  removeGuest = async () => {
    const {
      reservation,
    } = this.props;

    const { guestToRemove } = this.state;

    const guestIsHost = guestToRemove?.type === 'host';
    if (guestIsHost || !guestToRemove) {
      return;
    }

    try {
      const response = await deletePartyGuest({ guestToken: guestToRemove.token, resyToken: reservation.token });
      if (response) {
        this.setState({
          party: response,
          showRemoveGuestModal: false,
        });
      }
    } catch (error) {
      Logger.error(`[GuestInvite] error removing guest from reservation: ${error}`);
    }
  }

  toggleDeclineModal = () => {
    const {
      showDeclineModal,
    } = this.state;

    window.parent.postMessage({
      event: 'resyModalScrolltop',
    }, '*');

    this.setState({
      showDeclineModal: !showDeclineModal,
    });
  }

  renderRemoveSelfModal = () => {
    const { matchMedia } = this.props;
    const { showDeclineModal } = this.state;
    return (
      <Modal
        ariaHideApp={false}
        isOpen={showDeclineModal}
        onRequestClose={this.toggleDeclineModal}
        shouldCloseOnEsc
        shouldCloseOnOverlayClick
        styles={getAuthModalStyles(matchMedia)}
        onAfterClose={focusTrap.focusPage}
        onAfterOpen={focusTrap.focusModal}
      >
        <div className="InviteModal__header">
          <TranslationProvider
            translationKey="invite_decline_confirm_header"
          >
            <div className="InviteModal__title">Leave the reservation?</div>
          </TranslationProvider>
          <Button
            aria-label={getTranslation({ translationKey: 'close_booking', fallback: 'close booking' })}
            className="InviteModal__close"
            onClick={this.toggleDeclineModal}
            variant="no-style"
          >
            <CloseThick />
          </Button>
        </div>
        <div className="InviteModal__body">
          <TranslationProvider
            translationKey="invite_decline_confirm_body"
          >
            <p>Are you sure you want to leave the reservation?</p>
          </TranslationProvider>
        </div>
        <div className="InviteModal__buttons">
          <TranslationProvider
            translationKey="leave"
          >
            <Button
              className="InviteModal__buttons--decline"
              data-test-id={`${testId}-button-confirm_decline_invite`}
              onClick={this.removeSelf}
              variant="primary"
            >
              Leave
            </Button>
          </TranslationProvider>
          <TranslationProvider
            translationKey="invite_decline_confirm_no"
          >
            <Button
              data-test-id={`${testId}-button-dismiss_decline_invite`}
              onClick={this.toggleDeclineModal}
              variant="secondary"
            >
              Cancel
            </Button>
          </TranslationProvider>
        </div>
      </Modal>
    );
  }

  toggleRemoveGuestModal = (guest = null) => {
    const { reservation } = this.props;
    const {
      showRemoveGuestModal,
    } = this.state;
    let guestToRemove = guest;
    if (!guest.token) {
      guestToRemove = null;
    }

    const guestIsHost = guestToRemove?.type === 'host';
    if (guestIsHost) {
      return;
    }

    const guestIsSelf = guestToRemove?.token === reservation.attendee.token;
    if (guestIsSelf) {
      this.toggleDeclineModal();
    } else {
      this.setState({
        guestToRemove,
        showRemoveGuestModal: !showRemoveGuestModal,
      });
    }
  }

  renderRemoveGuestModal = () => {
    const { matchMedia } = this.props;
    const {
      guestToRemove,
      showRemoveGuestModal,
    } = this.state;

    return (
      <Modal
        ariaHideApp={false}
        isOpen={showRemoveGuestModal}
        onRequestClose={this.toggleRemoveGuestModal}
        shouldCloseOnEsc
        shouldCloseOnOverlayClick
        styles={getAuthModalStyles(matchMedia)}
      >
        <div className="InviteModal__header">
          <TranslationProvider
            translationKey="prompt_remove_guest_name"
            translationValues={{ name: guestToRemove.first_name }}
          >
            <div className="InviteModal__title">{`Remove ${guestToRemove.first_name}?`}</div>
          </TranslationProvider>
          <Button
            aria-label={getTranslation({ translationKey: 'close_booking', fallback: 'close booking' })}
            className="InviteModal__close"
            onClick={this.toggleRemoveGuestModal}
            variant="no-style"
          >
            <CloseThick />
          </Button>
        </div>
        <div className="InviteModal__body">
          <TranslationProvider
            translationKey="person_will_be_removed"
          >
            <p>This person will be removed from the reservation.</p>
          </TranslationProvider>
        </div>
        <div className="InviteModal__buttons">
          <TranslationProvider
            translationKey="remove"
          >
            <Button
              className="InviteModal__buttons--decline"
              data-test-id={`${testId}-button-confirm_remove_guest`}
              onClick={this.removeGuest}
              variant="secondary"
            >
              Remove
            </Button>
          </TranslationProvider>
          <TranslationProvider
            translationKey="invite_decline_confirm_no"
          >
            <Button
              data-test-id={`${testId}-button-dismiss_remove_guest`}
              onClick={this.toggleRemoveGuestModal}
              variant="secondary"
            >
              Nevermind
            </Button>
          </TranslationProvider>
        </div>
      </Modal>
    );
  }

  removeSelf = async () => {
    const {
      onClose,
    } = this.props;
    const { party } = this.state;

    const host = party.find((guest) => guest.type === 'host');

    try {
      await postPartyInviteDecline(host.token);
      onClose();
      window.parent.postMessage('reloadReservations', '*');
    } catch (error) {
      Logger.error(`[GuestInvite] error removing self from reservation: ${error}`);
    }
  }
  // End of Invite Your Party methods

  render() {
    const {
      activeSections,
      allergyGroup,
      allergyTags,
      allergyQuestionComplete,
      ariaLiveText,
      dietaryTags,
      error,
      isLoading,
      occasionTags,
      party,
      partyTags,
      selections,
      showDeclineModal,
      showRemoveGuestModal,
      userTags,
    } = this.state;

    const { reservation } = this.props;

    if (error) {
      return null;
    }

    const isHost = reservation?.attendee?.type === 'host';
    const shouldBail = !isLoading && ((!isHost && reservation?.venue?.hideAllergyQuestion) || (reservation?.venue?.hideOccasionQuestion && reservation?.venue?.hideAllergyQuestion && reservation?.venue?.hideSpecialRequestQuestion));

    if (shouldBail) {
      return null;
    }

    return (
      <div className="ReservationQuestionnaire">
        {this.renderIypModal()}
        <div className="ReservationQuestionnaire--inner-container">
          {isHost && !reservation?.venue?.hideOccasionQuestion
            && (
              <div
                className={cx(
                  'ReservationQuestionnaire__question',
                  { skeleton: isLoading }
                )}
                id="question-occasion"
              >
                <Button
                  variant="no-style"
                  className="ReservationQuestionnaire__question--header"
                  onClick={/* istanbul ignore next */ () => this.toggleSection(SECTIONS.OCCASION)}
                  type="button"
                  aria-expanded={activeSections[SECTIONS.OCCASION]}
                >
                  <Icon
                    name="Chevron"
                    className={cx({ 'chevron--down': activeSections[SECTIONS.OCCASION] })}
                    component={Chevron}
                  />
                  <TranslationProvider translationKey={this.getHeaderTranslationKey(SECTIONS.OCCASION)}>
                    <div className="ReservationQuestionnaire__question--title">
                      {this.getHeaderTranslationString(SECTIONS.OCCASION)}
                    </div>
                  </TranslationProvider>
                  {selections.occasion?.name
                    && (
                      <Icon
                        name="Checkmark"
                        className="Icon--Checkmark--green"
                        component={Checkmark}
                        height={20}
                        width={20}
                      />
                    )}
                  {selections.occasion?.name
                    ? (
                      <div
                        className="ReservationQuestionnaire__question--subtitle"
                      >
                        {selections.occasion.name}
                      </div>
                    )
                    : (
                      <TranslationProvider translationKey="celebrating_special">
                        <div
                          className="ReservationQuestionnaire__question--subtitle"
                        >
                          Celebrating something special?
                        </div>
                      </TranslationProvider>
                    )}
                </Button>

                {activeSections[SECTIONS.OCCASION]
                  && (
                    <div className="ReservationQuestionnaire__question--body">
                      <div className="button-container">
                        {occasionTags.map((occasion) => {
                          const activeSelection = selections.occasion?.name === occasion.name;
                          const occasionButtonLabel = activeSelection
                            ? getTranslation({ translationKey: `${occasion.name} selected`, fallback: `${occasion.name} selected` })
                            : getTranslation({ translationKey: `${occasion.name}`, fallback: `${occasion.name}` });

                          if (!OCCASION_ICON_MAP[occasion.icon]) {
                            return null;
                          }

                          return (
                            <Button
                              className={cx({ active: activeSelection })}
                              disabled={reservation?.pastOrFuture !== 'future'}
                              key={occasion.name}
                              onClick={/* istanbul ignore next */ () => this.setOccasion(occasion)}
                              variant="black"
                              aria-label={occasionButtonLabel}
                            >
                              <Icon
                                name={occasion.name}
                                component={OCCASION_ICON_MAP[occasion.icon]}
                              />
                              <span>{occasion.name}</span>
                            </Button>
                          );
                        })}
                        {occasionTags.length > 0
                          && (
                            <Button
                              className={cx({ active: selections.occasion?.id === 'goodTime' })}
                              disabled={reservation?.pastOrFuture !== 'future'}
                              onClick={/* istanbul ignore next */ () => this.setOccasion({ name: 'Just a good time', id: 'goodTime' })}
                              variant="black"
                            >
                              <TranslationProvider translationKey="just_good_time">
                                <span>Just a good time</span>
                              </TranslationProvider>
                            </Button>
                          )}
                      </div>
                    </div>
                  )}
                <hr className="ReservationQuestionnaire__separator" />
              </div>
            )}
          <ReservationQuestion
            additionalElements={activeSections[SECTIONS.PARTY] && this.renderPartyActions()}
            id="invite-your-party"
            isActive={activeSections[SECTIONS.PARTY]}
            isLoading={isLoading}
            onClick={() => this.toggleSection(SECTIONS.PARTY)}
            showSeparator={(!reservation?.venue?.hideAllergyQuestion || !reservation?.venue?.hideSpecialRequestQuestion)}
            subtitleText={getTranslation({
              translationKey: isHost ? 'invite_your_party_subtitle' : 'invite_your_party_subtitle_guest',
              fallback: isHost ? 'Invite guests to add their dining preferences.' : 'Guests joining the reservation.',
            })}
            titleText={getTranslation({
              translationKey: this.getHeaderTranslationKey(SECTIONS.PARTY),
              fallback: this.getHeaderTranslationString(SECTIONS.PARTY),
            })}
          >
            <GuestInvite
              ariaLiveText={ariaLiveText}
              copyLink={(context) => this.copyInviteLink(context)}
              party={party}
              renderRemoveGuestModal={this.renderRemoveGuestModal}
              renderRemoveSelfModal={this.renderRemoveSelfModal}
              reservation={reservation}
              showDeclineModal={showDeclineModal}
              showRemoveGuestModal={showRemoveGuestModal}
              toggleRemoveGuestModal={this.toggleRemoveGuestModal}
            />
          </ReservationQuestion>
          {!reservation?.venue?.hideAllergyQuestion
            && (
              <>
                <div
                  className={cx(
                    'ReservationQuestionnaire__question',
                    { skeleton: isLoading }
                  )}
                  id="question-allergies"
                >
                  <Button
                    disabled={isLoading}
                    variant="no-style"
                    className="ReservationQuestionnaire__question--header"
                    onClick={/* istanbul ignore next */ () => this.toggleSection(SECTIONS.ALLERGIES)}
                    type="button"
                    aria-expanded={activeSections[SECTIONS.ALLERGIES]}
                    aria-label={this.getHeaderButtonAriaLabel(SECTIONS.ALLERGIES)}
                  >
                    <Icon
                      name="Chevron"
                      className={cx({ 'chevron--down': activeSections[SECTIONS.ALLERGIES] })}
                      component={Chevron}
                    />
                    <TranslationProvider translationKey={this.getHeaderTranslationKey(SECTIONS.ALLERGIES)}>
                      <div className="ReservationQuestionnaire__question--title">
                        {this.getHeaderTranslationString(SECTIONS.ALLERGIES)}
                      </div>
                    </TranslationProvider>
                    {allergyQuestionComplete
                      && (
                        <Icon
                          name="Checkmark"
                          className="Icon--Checkmark--green"
                          component={Checkmark}
                          height={20}
                          width={20}
                        />
                      )}
                    {this.areAllergiesSet()
                      ? (
                        <div
                          className="ReservationQuestionnaire__question--subtitle"
                        >
                          <ClampLines
                            text={this.getAllergySubtitle()}
                            id="allergy-subtitle"
                            lines={1}
                            buttons={false}
                            ellipsis="..."
                          />
                          { }
                        </div>
                      )
                      : (
                        <TranslationProvider translationKey="your_someone_in_party">
                          <div
                            className="ReservationQuestionnaire__question--subtitle"
                          >
                            Your own or someone in your party.
                          </div>
                        </TranslationProvider>
                      )}
                  </Button>

                  {activeSections[SECTIONS.ALLERGIES]
                    && (
                      <div className="ReservationQuestionnaire__question--body">
                        {isHost
                          && (
                            <div className="group-toggle" role="tablist">
                              <TranslationProvider translationKey="for_you">
                                <Button
                                  aria-selected={allergyGroup === ALLERGY_GROUPS.YOU}
                                  aria-controls="for_you_panel"
                                  className={cx('group-toggle__button', {
                                    'group-toggle__button--active': allergyGroup === ALLERGY_GROUPS.YOU,
                                  })}
                                  onClick={() => this.toggleAllergyGroup(ALLERGY_GROUPS.YOU)}
                                  variant="no-style"
                                  id="for_you_tab"
                                  role="tab"
                                >
                                  {ALLERGY_GROUPS.YOU}
                                </Button>
                              </TranslationProvider>
                              <TranslationProvider translationKey="your_party">
                                <Button
                                  aria-selected={allergyGroup === ALLERGY_GROUPS.YOUR_PARTY}
                                  aria-controls="your_party_panel"
                                  className={cx('group-toggle__button', {
                                    'group-toggle__button--active': allergyGroup === ALLERGY_GROUPS.YOUR_PARTY,
                                  })}
                                  onClick={() => this.toggleAllergyGroup(ALLERGY_GROUPS.YOUR_PARTY)}
                                  variant="no-style"
                                  id="your_party_tab"
                                  role="tab"
                                >
                                  {ALLERGY_GROUPS.YOUR_PARTY}
                                </Button>
                              </TranslationProvider>
                            </div>
                          )}

                        {allergyGroup === ALLERGY_GROUPS.YOU
                          && (
                            <div
                              role={isHost && 'tabpanel'}
                              aria-labelledby={isHost && 'for_you_tab'}
                              id="for_you_panel"
                            >
                              <AllergyButtonGroup
                                disabled={reservation?.pastOrFuture !== 'future'}
                                allergyTags={allergyTags}
                                dietaryTags={dietaryTags}
                                saveAllergy={this.saveUserAllergy}
                                savedTags={userTags}
                              />
                            </div>
                          )}

                        {allergyGroup === ALLERGY_GROUPS.YOUR_PARTY
                          && (
                            <div
                              role={isHost && 'tabpanel'}
                              aria-labelledby={isHost && 'your_party_tab'}
                              id="your_party_panel"
                            >
                              <AllergyButtonGroup
                                disabled={reservation?.pastOrFuture !== 'future'}
                                allergyTags={allergyTags}
                                dietaryTags={dietaryTags}
                                saveAllergy={this.savePartyAllergy}
                                savedTags={partyTags}
                              />
                            </div>
                          )}

                        <TranslationProvider translationKey="additional_dietary_note">
                          <div className="allergy-note-title">Additional Dietary Note</div>
                        </TranslationProvider>

                        {allergyGroup === ALLERGY_GROUPS.YOU
                          && this.renderDietaryNoteInput(ALLERGY_GROUPS.YOU)}

                        {allergyGroup === ALLERGY_GROUPS.YOUR_PARTY
                          && this.renderDietaryNoteInput(ALLERGY_GROUPS.YOUR_PARTY)}

                        {this.areAllergiesSet()
                          ? this.filledContinueButton(this.submitDietaryNote)
                          : this.unfilledContinueButton(this.submitDietaryNote)}
                      </div>
                    )}
                </div>
                {!reservation?.venue?.hideSpecialRequestQuestion
                  && (
                    <hr className="ReservationQuestionnaire__separator" />
                  )}
              </>
            )}
          {isHost && !reservation?.venue?.hideSpecialRequestQuestion
            && (
              <div
                className={cx(
                  'ReservationQuestionnaire__question',
                  { skeleton: isLoading }
                )}
                id="question-special-request"
              >
                <Button
                  variant="no-style"
                  className="ReservationQuestionnaire__question--header"
                  onClick={/* istanbul ignore next */ () => this.toggleSection(SECTIONS.REQUESTS)}
                  type="button"
                  aria-expanded={activeSections[SECTIONS.REQUESTS]}
                >
                  <Icon
                    name="Chevron"
                    className={cx({ 'chevron--down': activeSections[SECTIONS.REQUESTS] })}
                    component={Chevron}
                  />
                  <TranslationProvider translationKey={this.getHeaderTranslationKey(SECTIONS.REQUESTS)}>
                    <div className="ReservationQuestionnaire__question--title">
                      {this.getHeaderTranslationString(SECTIONS.REQUESTS)}
                    </div>
                  </TranslationProvider>
                  {this.isSpecialRequestSet()
                    && (
                      <Icon
                        name="Checkmark"
                        className="Icon--Checkmark--green"
                        component={Checkmark}
                        height={20}
                        width={20}
                      />
                    )}
                  {this.isSpecialRequestSet()
                    ? (this.setActiveSpecialRequestSubtitle(activeSections, selections)
                    )
                    : (
                      <TranslationProvider translationKey="well_give_restaurants_heads_up">
                        <div
                          className="ReservationQuestionnaire__question--subtitle"
                        >
                          We&apos;ll give restaurants a heads up.
                        </div>
                      </TranslationProvider>
                    )}
                </Button>

                {activeSections[SECTIONS.REQUESTS]
                  && (
                    <div className="ReservationQuestionnaire__question--body">
                      <textarea
                        aria-label={addSpecialRequests}
                        className="additional-note"
                        disabled={reservation?.pastOrFuture !== 'future'}
                        id="special-request"
                        name="specialRequest"
                        onChange={this.setSpecialRequest}
                        placeholder={`${addSpecialRequests}...`}
                        defaultValue={this.isSpecialRequestSet() ? selections.specialRequest : ''}
                      />

                      {this.isSpecialRequestSet()
                        ? this.filledContinueButton(this.submitSpecialRequest)
                        : this.unfilledContinueButton(this.submitSpecialRequest)}
                    </div>
                  )}
              </div>
            )}
        </div>
      </div>
    );
  }
}

ReservationQuestionnaire.propTypes = {
  Analytics: PropTypes.shape({
    event: PropTypes.func.isRequired,
  }).isRequired,
  matchMedia: PropTypes.shape({
    xs: PropTypes.bool,
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool,
    ipadLandscape: PropTypes.bool,
    xl: PropTypes.bool,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  reservation: reservationShape.isRequired,
  sourceId: PropTypes.string,
  getUserThunk: PropTypes.func.isRequired,
  geoGateForDataDogExperiment: PropTypes.shape({
    ipdata: PropTypes.shape({
      countryCode: PropTypes.string,
    }),
    user: PropTypes.shape({
      userLocation: PropTypes.shape({
        countryCode: PropTypes.string,
        isNonUS: PropTypes.bool,
      }),
    }),
  }),
};
const mapStateToProps = (state) => {
  const {
    ipdata,
    matchMedia,
    resyRouter,
    user,
  } = state;

  return {
    geoGateForDataDogExperiment: { user, ipdata },
    matchMedia,
    sourceId: resyRouter?.sourceId,
  };
};

const mapDispatchToProps = { getUserThunk };
export { ReservationQuestionnaire as ComponentUnderTest, SECTIONS, ALLERGY_GROUPS };
export default connect(mapStateToProps, mapDispatchToProps)(ReservationQuestionnaire);
